import { formatInVenueZone } from 'utils/shared/time/formatInVenueZone';
import classNames from 'styles/utils/classNames';
import DateRail from './DateRail';

interface Props {
  /** ISO start date of the activity. */
  date: string;
  /** Renders the weekday (e.g. "WED") above the month — used by featured cards. */
  showWeekday?: boolean;
  /** Overrides the desktop column width (defaults to MyActivity's 11.25rem). */
  widthClassName?: string;
  /** IANA zone to render the date in (venue's); browser zone when omitted. */
  timezone?: string;
  /**
   * How the date reads below `lg`. `header` (default) keeps the full-width
   * `NOV 14` banner; `rail` swaps in the compact weekday-over-day rail the home
   * feed uses, leaving the desktop column untouched.
   */
  mobileVariant?: 'header' | 'rail';
}

export default function DateColumn({
  date,
  showWeekday = false,
  widthClassName = 'sm:w-[11.25rem]',
  timezone,
  mobileVariant = 'header',
}: Props) {
  const parsed = new Date(date);
  const fmt = (pattern: string) => formatInVenueZone(parsed, pattern, timezone);

  const isRail = mobileVariant === 'rail';

  // Mobile: a full-width horizontal header (`NOV 14`), or the compact rail when
  // asked for. Desktop: a centered left column, identical either way.
  return (
    <>
      {isRail && <DateRail date={date} timezone={timezone} className="lg:hidden" />}
      <div
        className={classNames(
          'flex shrink-0 items-center gap-3 max-sm:w-full sm:flex-col sm:justify-center sm:gap-0 sm:text-center',
          isRail && 'max-lg:hidden',
          widthClassName,
        )}
      >
        {showWeekday && (
          <span className="typography-informative-display-mega-primary-mobile uppercase leading-none text-color-text-lightmode-primary dark:text-color-text-darkmode-primary max-sm:text-[2.5rem]">
            {fmt('EEE')}
          </span>
        )}
        <span className="typography-informative-display-mega-secondary-mobile uppercase leading-none text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary max-sm:text-[2.5rem]">
          {fmt('MMM')}
        </span>
        <span className="typography-informative-display-primary-desktop not-italic leading-none text-color-text-lightmode-primary dark:text-color-text-darkmode-primary max-sm:text-[3.5rem]">
          {fmt('d')}
        </span>
      </div>
    </>
  );
}
